import React, { useState } from "react";
import { Box, Container, Stack } from "@mui/material";
import { Common, Custom } from "components";
import { useApp, useTheme } from "contexts";
import { Combines } from "helpers";
import { RocketLaunchpad } from "./RocketLaunchpad";
import { CircleRounded } from "@mui/icons-material";

type Props = {};

export const Footer = (props: Props) => {
  const { locale, t } = useApp();
  const { theme } = useTheme();

  const [isLaunching, setIsLaunching] = useState(false);

  const handleRocketLaunch = () => {
    if (isLaunching) return;
    setIsLaunching(true);

    // After 1.15s of engine ignition buildup, vibration, and smoke billowing,
    // as the rocket begins accelerating skyward, smoothly scroll back up through the atmosphere.
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 1150);

    // Reset rocket back onto the pad once the launch sequence completes
    setTimeout(() => {
      setIsLaunching(false);
    }, 4600);
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        zIndex: 2,
        width: "100%",
        mt: { xs: 24, md: 30 },
      }}
    >
      {/* Surface Complex 39A & Rocket Launchpad Sitting on Top of Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: "100%",
          left: 0,
          right: 0,
          height: { xs: 200, md: 240 },
          pointerEvents: "none",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          overflow: "visible",
          zIndex: 10,
        }}
      >
        {/* Ground Elevation Surface Strip */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 10,
            backgroundColor: theme.color.theme.color,
            borderTop: `2px solid ${theme.color.border.color}`,
            backgroundImage: `repeating-linear-gradient(90deg, ${theme.color.border.color} 0px, ${theme.color.border.color} 1px, transparent 1px, transparent 24px)`,
          }}
        />

        {/* Pad Elevation Telemetry & Interactive Rocket Component */}
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            pb: theme.spacing.sm,
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              alignItems: "center",
              gap: theme.spacing.xs,
              px: theme.spacing.xs,
              py: theme.spacing.xxs,
              backgroundColor: theme.color.background.accent,
              border: `1px solid ${theme.color.border.color}`,
              borderRadius: theme.border.square,
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxxs,
              fontWeight: theme.font.bold,
              color: theme.color.font.accent,
              letterSpacing: "0.08em",
            }}
          >
            <span>{t.atmosphere?.pad_elevation}</span>
          </Box>

          {/* Sub-component: Interactive Rocket Launchpad */}
          <RocketLaunchpad
            isLaunching={isLaunching}
            onLaunch={handleRocketLaunch}
          />
        </Container>
      </Box>

      {/* Subterranean Launch Bunker / Footer Bottom Bar */}
      <Box
        sx={{
          backgroundColor: theme.color.theme.color,
          borderTop: `1px solid ${theme.color.border.color}`,
          boxShadow: `0 -2px 10px rgba(0,0,0,0.05)`,
          py: theme.spacing.lg,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems="center"
            justifyContent="space-between"
            spacing={theme.spacing.md}
          >
            {/* Brand & Colophon */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              spacing={theme.spacing.md}
              textAlign={{ xs: "center", md: "left" }}
            >
              <Custom.Typography
                variant="body2"
                sx={{
                  color: theme.color.font.accent,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xs,
                }}
              >
                {Combines.interpolate(t.footer?.prefix, {
                  year: new Date().toLocaleDateString(locale, {
                    year: "numeric",
                  }),
                })}
              </Custom.Typography>
              <Box
                component="span"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  color: theme.color.border.color,
                }}
              >
                <CircleRounded sx={{ fontSize: 6 }} />
              </Box>
              <Custom.Typography
                variant="body2"
                sx={{
                  color: theme.color.font.accent,
                  fontFamily: '"Space Mono", monospace',
                  fontSize: theme.font.xs,
                }}
              >{t.footer?.tagline}</Custom.Typography>
            </Stack>

            {/* Social Anchors Mini Bar */}
            <Box>
              <Common.Social size="small" />
            </Box>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
